import { useRef, type ReactNode } from 'react';
import styles from './SegmentedTabs.module.css';

export type SegmentedTabItem<TValue extends string = string> = {
  value: TValue;
  /** CEVRILMIS etiket — sabit listeler `labelKey` saklar, cagri yerinde t() ile cevrilir. */
  label: ReactNode;
  /**
   * Sag ustte kucuk sayac (acik talep sayisi gibi).
   * `| undefined` ZORUNLU: tsconfig'de exactOptionalPropertyTypes acik, dolayisiyla
   * "henuz yuklenmedi" durumunu `count={undefined}` ile gecmek aksi halde tip hatasi verir.
   */
  count?: number | undefined;
  disabled?: boolean;
};

export type SegmentedTabsProps<TValue extends string = string> = {
  items: ReadonlyArray<SegmentedTabItem<TValue>>;
  value: TValue;
  onChange: (value: TValue) => void;
  /** Ekran okuyucu icin liste adi. */
  label: string;
  size?: 'sm' | 'md';
  /** Genis alanlarda tum satiri kaplasin mi. */
  block?: boolean;
  className?: string;
};

/**
 * Mobil taraftaki sekme cubugunun web karsiligi. ARIA tablist sozlesmesini
 * uygular: ok tuslariyla gezinme, Home/End, roving tabindex.
 */
export function SegmentedTabs<TValue extends string = string>({
  items,
  value,
  onChange,
  label,
  size = 'md',
  block = false,
  className,
}: SegmentedTabsProps<TValue>) {
  const listRef = useRef<HTMLDivElement>(null);

  const focusTab = (index: number): void => {
    const buttons = listRef.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]');
    buttons?.item(index)?.focus();
  };

  const moveSelection = (currentIndex: number, delta: number): void => {
    const count = items.length;
    if (count === 0) return;
    let next = currentIndex;
    for (let step = 0; step < count; step += 1) {
      next = (next + delta + count) % count;
      const candidate = items[next];
      if (candidate && candidate.disabled !== true) {
        onChange(candidate.value);
        focusTab(next);
        return;
      }
    }
  };

  const activeIndex = items.findIndex((item) => item.value === value);

  return (
    <div
      ref={listRef}
      role="tablist"
      aria-label={label}
      className={[styles.tabs, styles[size], block ? styles.block : undefined, className]
        .filter(Boolean)
        .join(' ')}
    >
      {items.map((item, index) => {
        const selected = item.value === value;
        return (
          <button
            key={item.value}
            type="button"
            role="tab"
            aria-selected={selected}
            disabled={item.disabled === true}
            // Roving tabindex: sekme cubugunun tamami TEK sekme duragidir.
            tabIndex={selected || (activeIndex === -1 && index === 0) ? 0 : -1}
            className={[styles.tab, selected ? styles.selected : undefined]
              .filter(Boolean)
              .join(' ')}
            onClick={() => {
              onChange(item.value);
            }}
            onKeyDown={(event) => {
              if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
                event.preventDefault();
                moveSelection(index, 1);
              } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
                event.preventDefault();
                moveSelection(index, -1);
              } else if (event.key === 'Home') {
                event.preventDefault();
                moveSelection(-1, 1);
              } else if (event.key === 'End') {
                event.preventDefault();
                moveSelection(0, -1);
              }
            }}
          >
            <span className={styles.text}>{item.label}</span>
            {item.count !== undefined ? <span className={styles.count}>{item.count}</span> : null}
          </button>
        );
      })}
    </div>
  );
}
